<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      As a personal trainer and/or fitness instructor, it's important to get to know your client’s
      fitness levels before you begin working with them, as well as assess them at various
      intervals.
    </p>
    <p class="mb-5 text-bold">Why Are Fitness Assessments Important?</p>
    <p class="mb-5">
      Fitness assessments provide trainers with a wealth of information and help the PT get to know
      their clients better. Here are just a few of the reasons why fitness assessments are
      important:
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <ul class="default-list">
          <li>Assessments help trainers and clients compare results over a period of time</li>
          <li>The trainer can plan an exercise program based on results.</li>
          <li>
            Help the trainer address mobility, stability, strength, endurance, or balance results
            that may increase the risk of injury
          </li>
          <li>They can increase client motivation</li>
          <li>Helps to build loyalty with current clients</li>
          <li>Cultivates strong relationships with new clients</li>
        </ul>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/fitness/activity_2_consult.jpg"
          alt="A personal trainer in a gym holding a clipboard with a client consultation form."
          class="mx-auto img-responsive"
        />
      </div>
    </div>

    <p class="mb-5">
      There are various methods used to assess overall fitness and health, and over your career
      you’ll become familiar with most of these.
    </p>
    <p class="mb-5">
      Below are a few simple methods you can use with minimal equipment. You can use these on
      yourself and your volunteer client.
    </p>
    <p class="mb-5">Let’s get started!</p>
    <h4 class="mb-5">Your Task:</h4>
    <p class="mb-5">
      In this task, you are going to record a range of details about both yourself and your client
      (choose a friend or family member – it can be the same person you used in Activity 1).
    </p>
    <p class="mb-5 text-italic">The Details</p>
    <p class="mb-5">
      Before performing a fitness assessment with a client, you’ll need to take note of some
      important details:
    </p>
    <ul class="default-list">
      <li>Sex</li>
      <li>Age</li>
      <li>Height</li>
      <li>Weight</li>
    </ul>

    <p class="mb-5 text-italic">
      Complete the details in the table below for yourself and your volunteer client:
    </p>
    <table class="table default-table">
      <thead>
        <tr class="subheader">
          <th width="10%"></th>
          <th class="text-center">Sex (M/F)</th>
          <th class="text-center">Age (Years)</th>
          <th class="text-center">Height (cm)</th>
          <th class="text-center">Weight (kg)</th>
        </tr>
      </thead>
      <tr class="body">
        <th scope="row" class="subheader">Me</th>
        <td v-for="q in 4" :key="'q1_1_' + q">
          <text-block v-model="data['question1_' + q]" :editable="isEditable" />
        </td>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">Client</th>
        <td v-for="q in 4" :key="'q1_2_' + q">
          <text-block v-model="data['question2_' + q]" :editable="isEditable" />
        </td>
      </tr>
    </table>
    <p class="mb-5">
      These details will be important for some of the other assessments you will make.
    </p>

    <p class="mb-5 text-bold">The Assessments</p>
    <h4 class="mb-5">1. Heart Rate</h4>
    <p class="mb-5">Resting Heart Rate</p>
    <p class="mb-5">
      Generally, the fitter a person is the lower the resting heart rate will be. The average
      resting heart rate is between 60 to 90 beats per minute. As a person gets fitter, their rest
      heart rate will lower over time.
    </p>
    <p class="mb-5">Watch this video to learn how to take a resting heart rate</p>
    <responsive-youtube-wrapper :image="videos[1].image" :video="videos[1].url" />

    <p class="mb-5">
      Follow the instructions below to take your own resting heart rate. Take your volunteer
      client’s resting heart rate, or have them take it themselves and share their BPM (beats per
      minute) with you.
    </p>

    <ul class="default-list">
      <li>Find the pulse - inside the wrist on the thumb side</li>
      <li>Count BPM (this is the resting heart rate)</li>
      <li>
        If necessary you can take the pulse for 30 seconds and double it (counting for a full minute
        gives the most accurate result)
      </li>
    </ul>
    <p class="mb-5">Record your Resting Heart Rate results in the table below</p>
    <table class="table default-table">
      <thead>
        <tr class="subheader">
          <th width="50%"></th>
          <th width="50%" class="text-center">Resting Heart Rate</th>
        </tr>
      </thead>
      <tr class="body">
        <th scope="row" class="subheader">Me</th>
        <td>
          <div class="overflow-hidden flex justify-center">
            <text-block
              v-model="data['question4']"
              :editable="isEditable"
              style="width: 80%; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 pl-2"><strong>bpm</strong></p>
          </div>
        </td>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">Client</th>
        <td>
          <div class="overflow-hidden flex justify-center">
            <text-block
              v-model="data['question5']"
              :editable="isEditable"
              style="width: 80%; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 pl-2"><strong>bpm</strong></p>
          </div>
        </td>
      </tr>
    </table>

    <h4 class="mb-5">Target Heart Rate</h4>
    <p class="mb-5">
      Exercise increases heart rate, and that’s a good thing - but if we raise the heart rate too
      high during exercise it can be dangerous. Likewise, if the heart rate doesn’t rise enough, it
      won’t have much of an impact on improving our health.
    </p>
    <p class="mb-5">
      This means we need to find the ‘sweet spot’ - known as the Target Heart Rate, that represents
      how fast the heart should be beating during exercise for the best results. Before you can use
      target heart rate, you need to calculate what it is, as it differs from person to person.
    </p>
    <h4 class="mb-5">How To Calculate Target Heart Rate</h4>
    <p class="mb-5">
      Target heart rate is calculated as a percentage of the maximum safe heart rate (usually
      between 50% and 85%). The maximum rate is based on a person’s age subtracted from 220.
    </p>
    <p class="mb-5">
      You can monitor heart rate and try to reach this target zone using a heart rate monitor which
      is sometimes in-built on equipment.
    </p>
    <table class="table default-table">
      <tr class="subheader">
        <th class="text-center">Example Calculation</th>
      </tr>
      <tr class="body">
        <td>
          <p class="mb-5">
            For a 50-year-old, the maximum heart rate is 220 minus 50, or 170 beats per minute.
          </p>
          <p class="mb-5">
            At a 50% exertion level, the target would be 50% of that maximum, or 85 beats per
            minute.
          </p>
          <p class="mb-5">At an 85% exertion level, the target would be 145 beats per minute.</p>
          <p class="mb-5">
            Therefore, the target heart rate that a 50-year-old would want to aim for during
            exercise is 85 to 145 beats per minute.
          </p>
          <p class="mb-5 text-italic">
            <u>Target Heart Rate Recommended Range</u> is from 85 bpm to 145 bpm.
          </p>
        </td>
      </tr>
    </table>

    <p class="mb-5">
      Learning the formula above will enable you to calculate the target heart rate for a client by
      yourself. However, many exercise machines will do the calculations for you when you input the
      necessary information.
    </p>
    <p class="mb-5">
      Another option is to use an online Target Heart Rate Calculator such as the one here:
    </p>

    <a href="https://www.active.com/fitness/calculators/heartrate" target="_blank">
      <centered-image-wrapper
        alt="Open the heart rate calculator (opens in a new tab)"
        :image="require('@/assets/images/experiences/fitness/heart_rate_calculator.jpg')"
      />
    </a>
    <p class="mb-5">
      Now calculate your own and your volunteer client’s Target Heart Rate Recommended Range (from
      50% to 85%) and record in the table below:
    </p>
    <table class="table default-table">
      <thead>
        <tr class="subheader">
          <th width="20%"></th>
          <th width="80%" class="text-center">Target Heart Rate Recommended Range</th>
        </tr>
      </thead>
      <tr class="body">
        <th scope="row" class="subheader">Me</th>
        <td>
          <div class="overflow-hidden flex justify-center">
            <p class="float-left py-2 px-2"><strong>From</strong></p>
            <text-block
              v-model="data['question6_1']"
              :editable="isEditable"
              style="width: 100px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 px-2"><strong>bpm to</strong></p>
            <text-block
              v-model="data['question6_2']"
              :editable="isEditable"
              style="width: 100px; float: left"
              pclass="my-2"
            />
          </div>
        </td>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">Client</th>
        <td>
          <div class="overflow-hidden flex justify-center">
            <p class="float-left py-2 px-2"><strong>From</strong></p>
            <text-block
              v-model="data['question7_1']"
              :editable="isEditable"
              style="width: 100px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 px-2"><strong>bpm to</strong></p>
            <text-block
              v-model="data['question7_2']"
              :editable="isEditable"
              style="width: 100px; float: left"
              pclass="my-2"
            />
          </div>
        </td>
      </tr>
    </table>

    <h4 class="mb-5">2. Body Mass Index</h4>

    <p class="mb-5">
      BMI, or Body Mass Index, is a simple calculation using a person's height and weight. The
      formula is BMI = kg/m2 where kg is a person's weight in kilograms and m2 is their height in
      metres squared. A BMI of 25.0 or more is overweight, while the healthy range is 18.5 to 24.9.
      BMI applies to most adults 18-65 years.
    </p>
    <p class="mb-5">
      This measurement has some limitations, as it doesn’t take a person’s body composition into
      consideration. For example, someone with a lot of muscle may have a BMI that is in the
      ‘overweight’ range or even the ‘obese’ range but still be healthy.
    </p>
    <p class="mb-5">
      This is because BMI does not differentiate between weight that is the result of body fat and
      weight which is the result of lean body mass including muscle.
    </p>
    <p class="mb-5">To find out some fascinating facts about BMI, check out this video:</p>
    <responsive-youtube-wrapper :image="videos[2].image" :video="videos[2].url" />

    <p class="mb-5 text-bold">Waist Circumference Measurement</p>
    <p class="mb-5">
      As we learned in the video, BMI doesn’t take into consideration how much of a person’s weight
      is excess fat around the waist.
    </p>
    <p class="mb-5">
      You can have a healthy BMI and still have too much tummy fat, meaning you're still at risk of
      developing diseases like heart disease or type 2 diabetes.
    </p>
    <p class="mb-5">
      Measuring the waist is a good way to check someone’s not carrying too much fat around their
      stomach. That’s why you are going to use the waist circumference measurement along with a BMI
      calculation in the next assessment.
    </p>
    <p class="mb-5">You’ll need:</p>

    <ul class="default-list">
      <li>A soft measuring tape</li>
    </ul>
    <p class="mb-5">
      Now, you’ll take a measurement of your own waist and your volunteer client’s waist. If you are
      working with them virtually, ask them to take the measurement themselves and send it to you.
    </p>
    <p class="mb-5">To measure your waist circumference:</p>

    <ul class="default-list">
      <li>Find the bottom of your ribs and the top of your hips</li>
      <li>
        Next, wrap a tape measure around your waist, midway between these points breathe out
        naturally before taking the measurement
      </li>
      <li>Read the measurement and write the number down.</li>
    </ul>
    <p class="mb-5">Check out this short video below for a more visual explanation:</p>
    <responsive-youtube-wrapper :image="videos[3].image" :video="videos[3].url" />

    <p class="mb-5">Record your own and your client’s waist circumference in the table below.</p>

    <table class="table default-table">
      <thead>
        <tr class="subheader">
          <th width="50%"></th>
          <th width="50%" class="text-center">Waist Circumference</th>
        </tr>
      </thead>
      <tr class="body">
        <th scope="row" class="subheader">Me</th>
        <td>
          <div class="overflow-hidden flex justify-center">
            <text-block
              v-model="data['question8']"
              :editable="isEditable"
              style="width: 80%; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 pl-2"><strong>cm</strong></p>
          </div>
        </td>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">Client</th>
        <td>
          <div class="overflow-hidden flex justify-center">
            <text-block
              v-model="data['question9']"
              :editable="isEditable"
              style="width: 80%; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 pl-2"><strong>cm</strong></p>
          </div>
        </td>
      </tr>
    </table>
    <p class="mb-5">
      Now you should have all the details you’ll need to use the BMI/Waist Circumference tool below.
    </p>
    <p class="mb-5">Go to the following link:</p>
    <p class="mb-5 text-center">
      <button
        class="btn btn-secondary btn-size-medium"
        @click="showIframePopup('https://www.healthdirect.gov.au/bmi-calculator')"
      >
        Open Tool
      </button>
    </p>
    <p class="mb-5">
      Input your height, weight, waist measurement, and gender. Then, hit the calculate button.
    </p>
    <p class="mb-5">
      This will calculate your BMI, place your waist measurement in a range, and categorise your
      risk level.
    </p>
    <p class="mb-5">Screenshot this information and upload it in the table below.</p>
    <p class="mb-5">
      Now, repeat the process using your volunteer client’s information. Upload their results in the
      table below.
    </p>
    <table class="table default-table">
      <thead>
        <tr class="subheader">
          <th></th>
          <th class="text-center">Results</th>
        </tr>
      </thead>
      <tr class="body">
        <th scope="row" class="subheader">My Results</th>
        <td>
          <text-block v-model="data['question10']" :editable="isEditable" pclass="my-2" />
        </td>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">Client’s Results</th>
        <td>
          <div class="overflow-hidden flex justify-center">
            <text-block v-model="data['question11']" :editable="isEditable" pclass="my-2" />
          </div>
        </td>
      </tr>
    </table>

    <vs-alert color="dark" title="Note" active="true">
      If you or your client’s results indicate a high risk, you should see your doctor to discuss
      your health as soon as possible.
    </vs-alert>
    <h4 class="my-5">3. Cardiovascular Fitness: The YMCA 3-Minute Step Test</h4>
    <p class="mb-5">
      There are lots of assessment methods that can be used to measure cardiovascular fitness. The
      YMCA 3-Minute Step Test is a great choice as it takes only (you’ve guessed it) 3 minutes to
      complete.
    </p>
    <p class="mb-5">You’ll Need:</p>

    <ul class="default-list">
      <li>A 12-inch step</li>
      <li>Stopwatch</li>
      <li>Metronome</li>
    </ul>
    <p class="mb-5">To learn how to do the YMCA 3-Minute Step Test, watch the following video:</p>
    <responsive-youtube-wrapper :image="videos[4].image" :video="videos[4].url" />

    <p class="mb-5 text-bold">To Complete the YMCA 3-Minute Step Test</p>

    <p class="mb-5">
      You and your volunteer client should complete this test by following the instructions below:
    </p>
    <ul class="default-list">
      <li>
        Record the resting heart rate of the participant before they warm up or begin the test.
      </li>
      <li>The participant should then complete a short warm-up before doing the assessment.</li>
      <li>
        The participant should step up with one foot, up with the other foot, then down with one
        foot, and down with the other foot to a cadence of 1 step per beat. The metronome should be
        set on 96 beats per minute, which equals 24 total steps each minute. Here’s a link to a
        video metronome:
      </li>
    </ul>

    <responsive-youtube-wrapper :image="videos[5].image" :video="videos[5].url" />
    <ul class="default-list">
      <li>
        After the three minutes is up, the client sits down immediately and their heart rate is
        taken for one minute.
      </li>
    </ul>

    <p class="mb-5">
      The one-minute heart rate is the score for the YMCA three-minute step test and is used to
      compare heart rate recovery by age group and sex. The categories range from very poor to
      excellent.
    </p>
    <p class="mb-5">
      Check you and your volunteer client’s results using the table below. Then, record your results
      in space provided.
    </p>

    <centered-image-wrapper
      :image="image1"
      classes="2/3"
      alt="YMCA 3-minute step test rating table: one-minute recovery heart rate by age group. Ages 18-25: excellent 50-76, good 79-84, above average 88-93, average 95-100, below average 102-107, poor 111-119, very poor 124-157. Ages 26-35: excellent 51-76, good 79-85, above average 88-94, average 96-102, below average 104-110, poor 114-121, very poor 126-161. Ages 36-45: excellent 49-76, good 80-88, above average 92-88, average 100-105, below average 108-113, poor 116-124, very poor 130-163. Ages 46-55: excellent 56-82, good 87-93, above average 95-101, average 103-111, below average 113-119, poor 121-126, very poor 131-159. Ages 56-65: excellent 60-77, good 86-94, above average 97-100, average 103-109, below average 111-117, poor 119-128, very poor 131-154. Ages 65 and over: excellent 59-81, good 87-92, above average 94-102, average 104-110, below average 114-118, poor 121-126, very poor 130-151."
    />
    <table class="table default-table">
      <tr class="header">
        <th colspan="2">YMCA 3-Minute Step Test Results (✓)</th>
      </tr>
      <tr class="subheader">
        <th scope="col" width="50%">Me</th>
        <th scope="col" width="50%">Client</th>
      </tr>
      <tr class="body">
        <td>
          <div class="vx-row mb-5">
            <div
              class="vx-col w-full"
              v-for="(answer, index) in ymca_answers"
              :key="'ymca1_' + index"
            >
              <vs-radio
                v-model="data.question12"
                class="checkbox_answer multiline"
                :vs-value="index"
                :vs-name="'ymca_1_' + index"
              >
                {{ answer }}</vs-radio
              >
            </div>
          </div>
        </td>
        <td>
          <div class="vx-row mb-5">
            <div
              class="vx-col w-full"
              v-for="(answer, index) in ymca_answers"
              :key="'ymca2_' + index"
            >
              <vs-radio
                v-model="data.question13"
                class="checkbox_answer multiline"
                :vs-value="index"
                :vs-name="'ymca_2_' + index"
              >
                {{ answer }}</vs-radio
              >
            </div>
          </div>
        </td>
      </tr>
    </table>

    <h4 class="mb-5">4. Muscular Endurance: The Push-up Test</h4>

    <p class="mb-5">
      This test measures muscular endurance. It’s important to note that this is different from
      testing muscular strength. This tests the number of repetitions of an exercise the participant
      can do - in this case, the push-up.
    </p>
    <p class="mb-5">
      The push-up test is used to test the endurance of the upper body, mainly the upper arm
      (triceps), chest (pectoralis), and shoulders (deltoid).
    </p>
    <p class="mb-5">
      This test is slightly different for men and women. While men complete the test with their toes
      on the ground, women complete a modified version with their knees on the ground.
    </p>
    <p class="mb-5">Here is a video explaining the full push-up:</p>
    <responsive-youtube-wrapper :image="videos[5].image" :video="videos[5].url" />

    <p class="mb-5">
      The participant completes as many repetitions as they can without stopping. The test is over
      when they are too tired to continue or they have poor form. The score is the total number of
      push-ups completed and is used to compare with others of similar age and gender.
    </p>
    <p class="mb-5">
      When you are sure you both understand how to do a push-up correctly, you and your volunteer
      client should do as many push-ups as you can!
    </p>
    <p class="mb-5">
      Using the table below, find the participant’s gender, age and number of push-ups completed by
      the participant. Then check the rating in the left-hand column (eg: Poor, Below Average,
      Average, Good, Excellent).
    </p>
    <centered-image-wrapper
      :image="image2"
      classes="2/3"
      alt="Push-up fitness test results table: number of push-ups by sex and age group. Men, ages 20-29: excellent 54 or more, good 45-54, average 35-44, poor 20-34, very poor 20 or fewer. Men 30-39: excellent 44 or more, good 35-44, average 24-34, poor 15-24, very poor 15 or fewer. Men 40-49: excellent 39 or more, good 30-39, average 20-29, poor 12-19, very poor 12 or fewer. Men 50-59: excellent 34 or more, good 25-34, average 15-24, poor 8-14, very poor 8 or fewer. Men 60 and over: excellent 29 or more, good 20-29, average 10-19, poor 5-9, very poor 5 or fewer. Women, ages 20-29: excellent 48 or more, good 34-48, average 17-33, poor 6-16, very poor 6 or fewer. Women 30-39: excellent 39 or more, good 25-39, average 12-24, poor 4-11, very poor 4 or fewer. Women 40-49: excellent 34 or more, good 20-34, average 8-19, poor 3-7, very poor 3 or fewer. Women 50-59: excellent 29 or more, good 15-29, average 6-14, poor 2-5, very poor 2 or fewer. Women 60 and over: excellent 19 or more, good 5-19, average 3-4, poor 1-2, very poor 1 or fewer."
    />

    <p class="mb-5">Record your rating and your client’s rating in the table below.</p>
    <table class="table default-table">
      <tr class="header">
        <th colspan="2">The Push-Up Test Results (✓)</th>
      </tr>
      <tr class="subheader">
        <th scope="col" width="50%">Me</th>
        <th scope="col" width="50%">Client</th>
      </tr>
      <tr class="body">
        <td>
          <div class="vx-row mb-5">
            <div
              class="vx-col w-full"
              v-for="(answer, index) in pushup_answers"
              :key="'pushup1_' + index"
            >
              <vs-radio
                v-model="data.question14"
                class="checkbox_answer multiline"
                :vs-value="index"
                :vs-name="'pushup_1_' + index"
              >
                {{ answer }}</vs-radio
              >
            </div>
          </div>
        </td>
        <td>
          <div class="vx-row mb-5">
            <div
              class="vx-col w-full"
              v-for="(answer, index) in pushup_answers"
              :key="'pushup2_' + index"
            >
              <vs-radio
                v-model="data.question15"
                class="checkbox_answer multiline"
                :vs-value="index"
                :vs-name="'pushup_2_' + index"
              >
                {{ answer }}</vs-radio
              >
            </div>
          </div>
        </td>
      </tr>
    </table>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/fitness/resting_heart_rate_splash.jpg'),
          url: 'https://www.youtube.com/embed/e9SAmMG6834',
        },
        2: {
          image: require('@/assets/images/experiences/fitness/bmi_splash.jpg'),
          url: 'https://www.youtube.com/embed/gBNsX6sgALM',
        },
        3: {
          image: require('@/assets/images/experiences/fitness/waste_circumfrence_splash.jpg'),
          url: 'https://www.youtube.com/embed/G2bmwZtZMv4',
        },
        4: {
          image: require('@/assets/images/experiences/fitness/step_test_splash.jpg'),
          url: 'https://www.youtube.com/embed/JiPrnsRk5nY',
        },
        5: {
          image: require('@/assets/images/experiences/fitness/activity_2_splash.jpg'),
          url: 'https://www.youtube.com/embed/AFnoWsn7VPc',
        },
        6: {
          image: require('@/assets/images/experiences/fitness/perfect_pushup_splash.jpg'),
          url: 'https://www.youtube.com/embed/IODxDxX7oi4',
        },
      },
      image1: require('@/assets/images/experiences/fitness/activity_2_img1.png'),
      image2: require('@/assets/images/experiences/fitness/activity2_img2.png'),
      ymca_answers: {
        1: 'Very Poor',
        2: 'Poor',
        3: 'Below Average',
        4: 'Average',
        5: 'Above Average',
        6: 'Good',
        7: 'Excellent',
      },
      pushup_answers: {
        1: 'Poor',
        2: 'Below Average',
        3: 'Average',
        4: 'Good',
        5: 'Excellent',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
};
</script>
